import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import PatientSidebar from "./PatientSidebar";
import SECTION_META from "../helper/sectionMeta";
import SECTIONS from "../sections";
import { RAIL_OPEN, RAIL_CLOSE } from "../sections/railEvents";
import "../styles/patientShell.scss";
import "../styles/sections.scss";
/* Loaded LAST so its @media (max-width: …) overrides win the cascade at the
   tablet/phone tiers. Desktop rules above stay authoritative — this file has
   nothing that applies above 1024px. */
import "../styles/phrResponsive.scss";

/**
 * The three-column section body of the patient screen: sidebar | content |
 * editor. Source: assembled section boards (e.g. Diagnosis 1982:12525) read via
 * Dev Mode MCP — Content Host 1528px, columns 260 / flex(832) / 340, 24px gaps.
 *
 * Controlled: the active section lives in PatientDetails so the per-section
 * title can render above the breadcrumb, matching Figma. Built sections come
 * from the registry; the rest fall back to placeholder slots.
 */
const PatientShell = ({ active, onSelect, patient, appointmentId, autoOpenAdd, onAutoOpenAdd }) => {
  // Panel state is local: it is a viewing preference for this visit, not
  // something the URL or the record should carry.
  const [railCollapsed, setRailCollapsed] = useState(false);
  const meta = SECTION_META[active] || { name: "" };
  const section = SECTIONS[active];
  const Content = section && section.Content;
  const Editor = section && section.Editor;

  // Some sections (the Lifestyle cards) are content-only — no 340px editor rail.
  // For those the content spans the full width and the aside is dropped; the
  // placeholder editor only shows for sections not yet built.
  const contentOnly = !!(section && section.contentOnly);

  // On-demand editors (Lab investigations): the 340px column is hidden and the
  // content takes its width until the editor emits RAIL_OPEN. The aside stays
  // MOUNTED while hidden — the editor owns the event subscription that opens
  // it, so unmounting it would leave nothing listening.
  const onDemand = !!(section && section.editorOnDemand);
  const [editorOpen, setEditorOpen] = useState(false);
  useEffect(() => setEditorOpen(false), [active]);
  useEffect(() => {
    const open = () => setEditorOpen(true);
    const close = () => setEditorOpen(false);
    EventService.addListener(RAIL_OPEN, open);
    EventService.addListener(RAIL_CLOSE, close);
    return () => {
      EventService.removeListener(RAIL_OPEN, open);
      EventService.removeListener(RAIL_CLOSE, close);
    };
  }, []);
  const editorHidden = onDemand && !editorOpen;

  return (
    // The id is the scroll anchor for a `?section=` deep link — the sections sit
    // below the header card and overview row, i.e. off-screen on arrival.
    <div
      id="ph-sections"
      className={`ph-shell-body${contentOnly ? " is-content-only" : ""}${
        railCollapsed ? " is-rail-collapsed" : ""
      }${editorHidden ? " is-editor-hidden" : ""}`}
    >
      <PatientSidebar
        active={active}
        onSelect={onSelect}
        collapsed={railCollapsed}
        onToggle={() => setRailCollapsed((c) => !c)}
      />

      <section className="ph-content" aria-label={`${meta.name} content`}>
        {Content ? (
          // Every section gets the patient. Sections still on hardcoded sample
          // data simply ignore it; the API-backed ones (Progress notes) need
          // patient.id to fetch. `appointmentId` is forwarded for sections
          // that stamp it onto writes (Lab investigations does — Add /
          // Batch / Update all persist it now); others ignore it silently.
          <Content
            patient={patient}
            appointmentId={appointmentId}
            /* Create Rx's "+ Add …": only the section it named, only once — see
               sections/common/useAutoOpenAdd. Sections without an add flow
               ignore it. */
            autoOpenAdd={!!autoOpenAdd && autoOpenAdd === active}
            onAutoOpenAdd={onAutoOpenAdd}
          />
        ) : (
          <div className="ph-slot-empty">
            <p>{meta.name} content will appear here.</p>
            <span>Sections are being built one at a time.</span>
          </div>
        )}
      </section>

      {!contentOnly && (
        <aside className="ph-editor" aria-label={`${meta.name} editor`}>
          {Editor ? (
            <Editor patient={patient} appointmentId={appointmentId} />
          ) : (
            <div className="ph-slot-empty is-editor">
              <p>Editor panel</p>
            </div>
          )}
        </aside>
      )}
    </div>
  );
};

PatientShell.propTypes = {
  active: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired,
  /** The loaded patient record, handed to every section. */
  patient: PropTypes.object,
  /** Currently-selected appointment id — forwarded to Content + Editor
   *  so sections that scope writes to an appointment (Lab
   *  investigations) can stamp it. Sections that don't care ignore it. */
  appointmentId: PropTypes.number,
  /** A section key whose add form should open once its list loads (Create Rx's
   *  "+ Add …"), or null. */
  autoOpenAdd: PropTypes.string,
  /** Called when that section has consumed it. */
  onAutoOpenAdd: PropTypes.func,
};

export default PatientShell;
